import React, { useState, useEffect, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import { Toaster } from '@/components/ui/sonner';
import LandingPage from '@/pages/LandingPage';
import ScheduleHealth from '@/pages/ScheduleHealth';
import WeeklyReflection from '@/pages/WeeklyReflection';
import NonNegotiables from '@/pages/NonNegotiables';
import CalendarSync from '@/pages/CalendarSync';
import TripsEvents from '@/pages/TripsEvents';
import LifeIntelligenceReport from '@/pages/LifeIntelligenceReport';
import Auth from '@/pages/Auth';
import Dashboard from '@/pages/Dashboard';
import Tasks from '@/pages/Tasks';
import Settings from '@/pages/Settings';
import Friends from '@/pages/Friends';
import Profile from '@/pages/Profile';
import Navbar from '@/components/Navbar';
import Tutorial from '@/components/Tutorial';
import AskSpaceCal from '@/components/AskSpaceCal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext(null);

// Shared counter — Tasks page increments this after any mutation;
// Dashboard watches it as a useEffect dependency and re-fetches.
export const TasksContext = React.createContext({ tasksVersion: 0, bumpTasks: () => {} });

function AppContent() {
  const navigate = useNavigate();
  const [auth, setAuth] = useState({
    token: localStorage.getItem('spacecal_token'),
    user: null,
    loading: true
  });
  const [showTutorial, setShowTutorial] = useState(false);
  const [tasksVersion, setTasksVersion] = useState(0);
  const bumpTasks = () => setTasksVersion(v => v + 1);

  useEffect(() => {
    const loadUser = async () => {
      const token = localStorage.getItem('spacecal_token');
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setAuth({ token, user: response.data, loading: false });
          
          if (!response.data.tutorial_completed) {
            setShowTutorial(true);
          }
        } catch (error) {
          localStorage.removeItem('spacecal_token');
          setAuth({ token: null, user: null, loading: false });
        }
      } else {
        setAuth({ token: null, user: null, loading: false });
      }
    };
    loadUser();
  }, []);

  useEffect(() => {
    if (!auth.token || !auth.user) return;
    const off = -new Date().getTimezoneOffset();
    const name = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (auth.user.tz_offset_minutes !== null && auth.user.tz_offset_minutes !== undefined) return;  // keep a saved time zone
    axios.put(`${API}/auth/timezone`, { tz_offset_minutes: off, timezone_name: name },
      { headers: { Authorization: `Bearer ${auth.token}` } }).catch(() => {});
  }, [auth.token, auth.user]);

  const login = (token, user) => {
    localStorage.setItem('spacecal_token', token);
    setAuth({ token, user, loading: false });
    if (!user.tutorial_completed) {
      setShowTutorial(true);
    }
  };

  const logout = () => {
    localStorage.removeItem('spacecal_token');
    setAuth({ token: null, user: null, loading: false });
    navigate('/');
  };

  if (auth.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#0A0E27,#050816)' }}>
        <div className="text-center">
          <div className="w-10 h-10 rounded-full border-2 border-t-transparent animate-spin mx-auto mb-3"
            style={{ borderColor: '#00D9FF', borderTopColor: 'transparent' }}/>
          <p className="text-gray-500 text-sm">Loading SpaceCal.ai…</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ auth, login, logout }}>
    <TasksContext.Provider value={{ tasksVersion, bumpTasks }}>
      <div className="App">
        <Toaster position="top-right" />
        {auth.user && showTutorial && !auth.user.tutorial_completed && (
          <Tutorial onClose={() => setShowTutorial(false)} />
        )}
        
        {auth.user && <Navbar />}
        {auth.user && <AskSpaceCal />}
        
        <Routes>
          <Route path="/" element={auth.user ? <Navigate to="/dashboard" /> : <LandingPage />} />
          <Route path="/auth" element={auth.user ? <Navigate to="/dashboard" /> : <Auth />} />
          <Route path="/dashboard" element={auth.user ? <Dashboard /> : <Navigate to="/auth" />} />
          <Route path="/tasks" element={auth.user ? <Tasks /> : <Navigate to="/auth" />} />
          <Route path="/friends" element={auth.user ? <Friends /> : <Navigate to="/auth" />} />
          <Route path="/profile/:userId" element={auth.user ? <Profile /> : <Navigate to="/auth" />} />
          <Route path="/settings" element={auth.user ? <Settings /> : <Navigate to="/auth" />} />
          <Route path="/health" element={auth.user ? <ScheduleHealth /> : <Navigate to="/auth" />} />
          <Route path="/reflection" element={auth.user ? <WeeklyReflection /> : <Navigate to="/auth" />} />
          <Route path="/non-negotiables" element={auth.user ? <NonNegotiables /> : <Navigate to="/auth" />} />
          <Route path="/trips" element={auth.user ? <TripsEvents /> : <Navigate to="/auth" />} />
          <Route path="/calendar-sync" element={auth.user ? <CalendarSync /> : <Navigate to="/auth" />} />
          <Route path="/life-report" element={auth.user ? <LifeIntelligenceReport /> : <Navigate to="/auth" />} />
        </Routes>
      </div>
    </TasksContext.Provider>
    </AuthContext.Provider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;